<template>
  <div class="app-page__container app-page__container--no-sidebar">
    <div v-if="!isLoading">
      <h5 class="title is-5">
        {{ activeTeam.teamName }}
      </h5>
      <div class="card">
        <div class="card-content">
          <form @submit.prevent="handleSubmitEditTeam">
            <b-field
              :label="editTeamForm.teamName.label"
              :type="formErrors[editTeamForm.teamName.name] ? 'is-danger' : ''"
              :message="formErrors[editTeamForm.teamName.name] || ''"
            >
              <b-input
                v-model="editTeamForm.teamName.value"
                :disabled="isFormSubmissionInProgress"
                @input="handleTouchForm"
              />
            </b-field>
            <!-- STANDUP DAYS -->
            <b-field :label="editTeamForm.standupMeetingDays.label">
              <div class="columns is-mobile is-marginless">
                <div
                  v-for="(day, index) in TimeAndDateConstants.daysOfTheWeek"
                  :key="day.id"
                  :class="[
                    'column',
                    'u-p-y-0',
                    {'u-p-l-0': index === 0},
                    {'u-p-r-0': index === 6},
                    'has-text-centered',
                    'weekday-picker__weekday-column'
                  ]"
                >
                  <div
                    :class="[
                      'button',
                      'is-fullwidth',
                      editTeamForm.standupMeetingDays.value.includes(day.id) ? 'is-primary' : 'is-outlined',
                      'has-text-weight-bold',
                      'weekday-picker__weekday-column__text-wrapper'
                    ]"
                    role="checkbox"
                    :aria-checked="editTeamForm.standupMeetingDays.value.includes(day.id)"
                    tabindex="0"
                    @click.prevent="handleSelectStandupDay(day.id)"
                    @keyup.enter="handleSelectStandupDay(day.id)"
                  >
                    {{ day.abbreviation }}
                  </div>
                </div>
              </div>
            </b-field>

            <!-- STANDUP TIME -->
            <div class="field columns u-m-y-0">
              <b-field :label="editTeamForm.standupMeetingTime.label" class="column u-p-y-0">
                <b-select name="standupTime" v-model="editTeamForm.standupMeetingTime.value" expanded>
                  <option
                    v-for="hour in TimeAndDateConstants.businessHours"
                    :value="hour.id"
                    :key="hour.id"
                  >
                    {{ hour.label }}
                  </option>
                </b-select>
              </b-field>
              <b-field :label="editTeamForm.timeZone.label" class="column u-p-y-0 u-m-b">
                <b-select name="teamTimeZone" v-model="editTeamForm.timeZone.value" expanded>
                  <option
                    v-for="timeZone in TimeAndDateConstants.timeZones"
                    :value="timeZone.id"
                    :key="timeZone.id"
                  >
                    {{ `${timeZone.abbreviation} ${timeZone.area}/${timeZone.location} ${timeZone.utcOffset}` }}
                  </option>
                </b-select>
              </b-field>
            </div>

            <!-- STANDUP FORMAT -->
            <b-field :label="editTeamForm.standupReportingDays.label" :addons="false">
              <b-field
                v-for="reportingDay in TimeAndDateConstants.standupReportingDays"
                :key="reportingDay.id"
                class="u-m-b-small"
              >
                <b-checkbox
                  v-model="editTeamForm.standupReportingDays.value"
                  :native-value="reportingDay.id"
                  :disabled="editTeamForm.standupReportingDays.value === 1
                    && editTeamForm.standupReportingDays.value.includes(reportingDay.id)"
                >
                  {{ titleCase(reportingDay.label) }}
                  <span class="has-text-grey is-size-7">
                    &nbsp;
                    {{ reportingDay.isRecommended ? 'Recommended for most teams' : '' }}
                  </span>
                </b-checkbox>
              </b-field>
            </b-field>

            <b-field :label="editTeamForm.isPrivate.label" class="u-m-b-small">
              <b-switch
                size="is-small"
                v-model="editTeamForm.isPrivate.value"
              >
                <span class="has-text-grey is-size-7">
                  &nbsp;
                  {{
                    activeTeam.isPrivate
                      ? 'Hold on! This can reveal sensitive information if turned off.'
                      : 'Hold on! By making this team private ONLY members of that team will have access to goals.'
                  }}
                </span>
              </b-switch>
            </b-field>

            <!-- INTEGRATIONS -->
            <b-field label="Integrations" :addons="false">
            <p v-if="activeTeam.slackData">
              Slack Workspace <strong>{{ activeTeam.slackData.team.name }}</strong> is connected to StandupPro.
              <a
                @keyup.enter="handleDisconnectSlack()"
                @click.prevent="handleDisconnectSlack()"
                :disabled="isFormSubmissionInProgress"
              >
                Delete integration?
              </a>
              <b-field
                class="u-m-b-small u-m-t-small"
              >
                <b-checkbox
                  v-model="editTeamForm.slackMeetingReminder.value"
                >
                  Meeting Reminder
                  <span class="has-text-grey is-size-7">
                    &nbsp;
                    Recieve a reminder in Slack a few minutes before Standup with a link to join
                  </span>
                </b-checkbox>
              </b-field>
              <b-field
                class="u-m-b-small u-m-t-small"
              >
                <b-checkbox
                  v-model="editTeamForm.slackGoalNotification.value"
                >
                  Goal Notifications
                  <span class="has-text-grey is-size-7">
                    &nbsp;
                    Get updates as team members add, update, and mark goals.
                  </span>
                </b-checkbox>
              </b-field>
            </p>
            <div v-else class="columns">
              <div class="column">
                Connect your Slack Workspace to StandupPro.
                Get notified when team members add and update goals,
                reminders when daily standup is about to begin, and more!
              </div>
              <div class="column is-narrow">
                <a @click.prevent="confirmConnectSlack()" @keyup.enter="confirmConnectSlack()">
                  <img
                    alt="Add to Slack"
                    height="40"
                    width="139"
                    src="https://platform.slack-edge.com/img/add_to_slack.png"
                    srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x,
                      https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
                  />
                </a>
              </div>
            </div>
            </b-field>

            <!-- TEAM MEMBERS -->
            <p class="edit-team__team-members__title">Team Members</p>
            &nbsp;
            <b-button
              type="is-info"
              outlined
              icon-left="plus"
              size="is-small"
              @click.prevent="handleClickInviteUser"
            >
              Invite User
            </b-button>
            <div class="card card-content u-p-l-0">
              <div v-if="teamUsers.length || teamInvitations.length">
                <div v-for="user in teamUsers" :key="user.id" class="edit-team__member-row">
                  <user-list-row :active-user-id="user.id" />
                  <b-button
                    v-if="isTeamLeader"
                    :type="isUserOOO(user, todayISO) ? 'is-warning' : 'is-light'"
                    size="is-small"
                    class="edit-team__ooo-button"
                    @click.prevent="$router.push(`/users/edit-profile/${user.id}`)"
                  >
                    OOO
                  </b-button>
                </div>
                <user-list-row
                  v-for="invitation in teamInvitations"
                  :key="invitation.id"
                  :active-invitation-id="invitation.id"
                />
              </div>
              <div v-else class="has-text-centered u-p-a">
                <p class="has-text-grey-light is-size-6">No team members.</p>
              </div>
            </div>
            <b-field class="has-text-right u-p-t">
              <b-button
                type="is-info"
                native-type="submit"
                :loading="isFormSubmissionInProgress"
              >
                Update Team
              </b-button>
            </b-field>
          </form>
        </div>
      </div>
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
    </div>
  </div>
</template>

<script>
import AppConfig from '@/plugins/config/AppConfig';
import { mapState, mapGetters, mapActions } from 'vuex';
import * as TeamActions from '@/store/actions/Team.actions';
import * as UserActions from '@/store/actions/User.actions';
import * as InvitationActions from '@/store/actions/Invitation.actions';
import { dateToISO, isUserOOO } from '@/helpers/dateHelpers';
import * as ValidationService from '@/services/Validation.service';
import * as ApiService from '@/services/Api.service';
import * as TimeAndDateConstants from '@/constants/TimeAndDateConstants';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { titleCase } from '@/helpers/stringHelpers';
import { confirmLeaveWithUnsavedChanges } from '@/helpers/formHelpers';
import { getUserTeams } from '@/helpers/teamHelpers';
import UserListRow from '@/components/UserListRow.vue';

const { config } = AppConfig.load();

export default {
  name: 'EditTeamView',

  components: {
    UserListRow,
  },

  data() {
    return {
      TimeAndDateConstants,
      editTeamForm: {
        teamName: {
          label: 'Team Name',
          name: 'Team Name',
          value: '',
          required: true,
        },
        standupMeetingDays: {
          label: 'Standup Meeting Days',
          name: 'Standup Meeting Days',
          value: null,
          required: true,
        },
        standupReportingDays: {
          label: 'Standup Reporting Days',
          name: 'Standup Reporting Days',
          value: null,
          required: true,
        },
        standupMeetingTime: {
          label: 'Standup Meeting Time',
          name: 'Standup Meeting Time',
          value: null,
          required: true,
        },
        timeZone: {
          label: 'Time Zone',
          name: 'Time Zone',
          value: null,
          required: true,
        },
        isPrivate: {
          label: 'This is a private team',
          name: 'Private Team',
          value: null,
          required: false,
        },
        slackMeetingReminder: {
          label: 'Standup Meeting Reminder',
          name: 'Standup Meeting Reminder',
          value: true,
          required: false,
        },
        slackGoalNotification: {
          label: 'Goal Create/Update Notifications',
          name: 'Goal Create/Update Notifications',
          value: true,
          required: false,
        },
      },
      formErrors: {},
      isFormTouched: false,
      isFormSubmissionInProgress: false,
      isTeamLoading: true,
      areTeamUsersLoading: true,
      areTeamInvitationsLoading: true,
    };
  },

  computed: {
    ...mapState({
      teams: state => state.team.teams,
      users: state => state.user.users,
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUser',
      'pendingInvitations',
    ]),
    activeTeam() {
      return this.teams[this.$route.params.teamId];
    },
    teamUsers() {
      return Object.values(this.users).filter(user => this.activeTeam.userIds.includes(user.id));
    },
    teamInvitations() {
      return Object.values(this.pendingInvitations).filter(invitation => invitation.teamId === this.activeTeam.id);
    },
    updatedTeamFormPayload() {
      return {
        teamName: this.editTeamForm.teamName.value,
        standupMeetingDayIds: this.editTeamForm.standupMeetingDays.value,
        standupMeetingTimeId: this.editTeamForm.standupMeetingTime.value,
        standupReportingDayIds: this.editTeamForm.standupReportingDays.value,
        timeZoneId: this.editTeamForm.timeZone.value,
        isPrivate: this.editTeamForm.isPrivate.value,
        slackMeetingReminder: this.editTeamForm.slackMeetingReminder.value,
        slackGoalNotification: this.editTeamForm.slackGoalNotification.value,
      };
    },
    isLoading() {
      return this.isTeamLoading || this.areTeamUsersLoading || this.areTeamInvitationsLoading;
    },
    isTeamLeader() {
      return this.activeTeam && this.loggedInUser.id === this.activeTeam.teamLeaderId;
    },
    todayISO() {
      return dateToISO(new Date());
    },
    slackConnectUrl() {
      return 'https://slack.com/oauth/v2/authorize?'
        + `client_id=${config.slack.client_id}&`
        + `redirect_uri=https://${config.app_base_url}/slack/incoming&`
        + `state=${this.$route.params.teamId}&`
        + 'scope=incoming-webhook,channels:join,app_mentions:read,channels:read,'
        + 'channels:manage,chat:write,commands,team:read,users:read&user_scope=';
    },
  },

  created() {
    this.fetchInitialData();
  },

  beforeRouteLeave (to, from, next) { // eslint-disable-line
    confirmLeaveWithUnsavedChanges(this.isFormTouched, next);
  },

  methods: {
    titleCase,
    getUserTeams,
    isUserOOO,
    ...mapActions({
      fetchTeamsByIds: TeamActions.FETCH_TEAMS_BY_IDS,
      fetchWorkspaceUsersByIds: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchInvitationsByTeamId: InvitationActions.FETCH_INVITATIONS_BY_TEAM_ID,
      updateTeam: TeamActions.UPDATE_TEAM,
    }),
    confirmConnectSlack() {
      this.$buefy.dialog.confirm({
        message: `We're about to redirect you to Slack in order to grant permissions 
          for the StandupPro app.<br><br> This will prompt for a channel that we can 
          post reminders to. We recommend a  dedicated <strong>#standup</strong> 
          channel for your team.  <br><br> If you don't yet have a channel for standup, 
          you'll need to create one and come back before connecting. <br><br> If you'd
          like for StandupPro to post to a private channel, select it on the next screen
          and then <strong>/invite @StandupPro</strong> in the channel after connecting 
          Slack.`,
        confirmText: 'Got it, lets go!',
        onConfirm: () => window.location.replace(this.slackConnectUrl),
      });
    },
    async handleDisconnectSlack() {
      this.isFormSubmissionInProgress = true;

      try {
        await ApiService.disconnectSlackWorkspace(this.activeTeam.slackData.access_token);
      } catch (error) {
        // do nothing, remove slackData from team regardless of error
      }

      const updateTeamPayload = {
        teamId: this.activeTeam.id,
        updatedTeam: {
          slackData: null,
        },
      };

      await this.updateTeam(updateTeamPayload);
      this.isFormSubmissionInProgress = false;
    },
    handleClickInviteUser() {
      this.$router.push('/invite-user');
    },
    handleTouchForm() {
      this.isFormTouched = true;
    },
    handleSelectStandupDay(dayOfTheWeekId) {
      if (this.editTeamForm.standupMeetingDays.value.includes(dayOfTheWeekId)) {
        this.editTeamForm.standupMeetingDays.value = [...this.editTeamForm.standupMeetingDays.value.filter(dayId => dayId !== dayOfTheWeekId)]; // eslint-disable-line max-len
      } else {
        this.editTeamForm.standupMeetingDays.value.push(dayOfTheWeekId);
      }
    },
    setInitialFormState() {
      this.editTeamForm.teamName.value = this.activeTeam.teamName;
      this.editTeamForm.standupMeetingDays.value = [...this.activeTeam.standupMeetingDayIds];
      this.editTeamForm.standupReportingDays.value = [...this.activeTeam.standupReportingDayIds];
      this.editTeamForm.timeZone.value = this.activeTeam.timeZoneId;
      this.editTeamForm.standupMeetingTime.value = this.activeTeam.standupMeetingTimeId;
      this.editTeamForm.isPrivate.value = this.activeTeam.isPrivate;
      this.editTeamForm.slackMeetingReminder.value = this.activeTeam.slackMeetingReminder;
      this.editTeamForm.slackGoalNotification.value = this.activeTeam.slackGoalNotification;
    },
    async handleSubmitEditTeam() {
      this.formErrors = ValidationService.runFormValidation({ ...this.editTeamForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;

      await this.updateTeam({ teamId: this.activeTeam.id, updatedTeam: { ...this.updatedTeamFormPayload } });
      this.isFormSubmissionInProgress = false;
    },
    async fetchInitialData() {
      await Promise.all([
        this.fetchTeamsByIds({ teamIds: [this.$route.params.teamId] }),
        this.fetchInvitationsByTeamId({ teamId: this.$route.params.teamId }),
      ]);
      this.isTeamLoading = false;
      this.areTeamInvitationsLoading = false;

      this.setInitialFormState();

      await this.fetchWorkspaceUsersByIds({
        userIds: this.activeTeam.userIds,
        workspaceId: this.activeWorkspace.id,
      });
      this.areTeamUsersLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-team__team-members__title {
  vertical-align: sub;
  display: inline;
  font-weight: bold;
}

.edit-team__member-row {
  display: flex;
  align-items: center;

  > *:first-child {
    flex: 1;
  }
}

.edit-team__ooo-button {
  margin-right: $global-whitespace-small;
  flex-shrink: 0;
}
</style>
